import { createContext, use } from 'react';
import invariant from 'tiny-invariant';

import type { Design } from '@cadstrom/sigma-engine-shared';
import type { Dispatch, SetStateAction } from 'react';

/** Identifies the design (and optional multi-instance) currently shown in the CAD renderer. */
export interface ActiveDesignSelection {
	designId: string;
	instanceId?: string;
}

export type ActiveDesignContextValue = {
	activeDesign: Design;
};

/**
 * The design currently shown in the CAD renderer, resolved from the active selection. Only
 * provided while a design is active — consumers that can render with no active design should
 * pass `required: false` to {@link useActiveDesignContext}.
 */
export const ActiveDesignContext = createContext<ActiveDesignContextValue | null>(null);
export function useActiveDesignContext(): ActiveDesignContextValue;
export function useActiveDesignContext(required: false): ActiveDesignContextValue | undefined;
export function useActiveDesignContext(required = true) {
	const context = use(ActiveDesignContext);
	if (!context && !required) {
		return undefined;
	}

	invariant(context, 'ActiveDesignContext is not provided');
	return context;
}

export type ActiveDesignSelectionContextValue = {
	activeSelection: ActiveDesignSelection | null;
	setActiveSelection: Dispatch<SetStateAction<ActiveDesignSelection | null>>;
};

/**
 * Read/write access to the active-design selection. Unlike {@link ActiveDesignContext}, this is
 * always provided (even while no design is active) so components can change the active design.
 */
export const ActiveDesignSelectionContext = createContext<ActiveDesignSelectionContextValue | null>(null);

/**
 * Returns the active-design selection state and its setter.
 *
 * @returns The current selection (or `null` when no design is active) and the state setter.
 */
export function useActiveDesignSelectionContext(): ActiveDesignSelectionContextValue {
	const context = use(ActiveDesignSelectionContext);
	invariant(context, 'ActiveDesignSelectionContext is not provided');
	return context;
}
